import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Highlight from "../Highlight";
import { buildApiUrl } from "../../lib/utils";

const AVATAR_BGS = [
  "bg-coral-500",
  "bg-[#3D8BFF]",
  "bg-plum-400",
  "bg-lime-300",
  "bg-sun-400",
  "bg-ink-v2",
];

// Some records come back with multi-pass HTML encoding (e.g. "&amp;amp;amp;")
const decodeEntities = (value) => {
  let text = String(value || "");
  for (let i = 0; i < 4 && text.includes("&"); i += 1) {
    const next = text
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, " ");
    if (next === text) break;
    text = next;
  }
  return text.trim();
};

const getInitials = (freelancer) => {
  const first = (freelancer.first_name || "").trim();
  const last = (freelancer.last_name || "").trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return initials || (freelancer.name || "?").trim().charAt(0).toUpperCase();
};

const AVAILABILITY = {
  "full-time": { label: "\u25CF Available now", bg: "bg-lime-300" },
  "part-time": { label: "\u25CF Part-time", bg: "bg-sun-400" },
};

const mapFreelancer = (freelancer, index) => {
  const availability =
    AVAILABILITY[freelancer.availability] || {
      label: "\u25CF Available",
      bg: "bg-sun-400",
    };

  return {
    id: freelancer.id || freelancer._id,
    photo: (freelancer.avatar_url || "").trim(),
    initials: getInitials(freelancer),
    avatarBg: AVATAR_BGS[index % AVATAR_BGS.length],
    name: decodeEntities(freelancer.name),
    role: decodeEntities(freelancer.title),
    avail: availability.label,
    availBg: availability.bg,
    blurb: decodeEntities(freelancer.bio),
    skills: (freelancer.skills || []).slice(0, 3).map((s) => s.name),
    rate: freelancer.hourly_rate ? `$${freelancer.hourly_rate}` : null,
    loc: decodeEntities(freelancer.location),
    videoUrl: (freelancer.video_url || "").trim(),
  };
};

// Each slide takes a full column on mobile, 2-up on tablet and 3-up on desktop
// (gap-6 = 24px between slides).
const SLIDE_CLASS =
  "w-[86%] shrink-0 snap-start min-[560px]:w-[70%] min-[900px]:w-[calc((100%-24px)/2)] min-[1100px]:w-[calc((100%-48px)/3)]";

function TalentCardSkeleton() {
  return (
    <div
      className={`${SLIDE_CLASS} flex animate-pulse flex-col gap-4 rounded-3xl border-[1.5px] border-ink-v2 bg-cream-100 p-7`}
    >
      <div className="flex items-center gap-3">
        <div className="h-[52px] w-[52px] shrink-0 rounded-full bg-warm-200" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 w-2/3 rounded bg-warm-200" />
          <div className="h-3 w-1/2 rounded bg-warm-200" />
        </div>
      </div>
      <div className="flex gap-2">
        <div className="h-6 w-20 rounded-full bg-warm-200" />
        <div className="h-6 w-16 rounded-full bg-warm-200" />
        <div className="h-6 w-24 rounded-full bg-warm-200" />
      </div>
      <div className="h-[200px] rounded-2xl bg-warm-200" />
      <div className="space-y-2">
        <div className="h-3 w-full rounded bg-warm-200" />
        <div className="h-3 w-4/5 rounded bg-warm-200" />
      </div>
      <div className="h-11 rounded-full bg-warm-200" />
    </div>
  );
}

export default function TalentGrid() {
  const [talent, setTalent] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const syncArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    setCanScrollLeft(track.scrollLeft > 8);
    setCanScrollRight(track.scrollLeft < maxScroll - 8);
  }, []);

  const scrollByPage = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.firstElementChild;
    const step = slide ? slide.offsetWidth + 24 : track.clientWidth;
    track.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  useEffect(() => {
    syncArrows();
    window.addEventListener("resize", syncArrows);
    return () => window.removeEventListener("resize", syncArrows);
  }, [syncArrows, talent, loading]);

  useEffect(() => {
    const controller = new AbortController();

    const loadTalent = async () => {
      try {
        const response = await fetch(
          buildApiUrl(
            "/api/v1/freelancers/search?limit=6&has_video=true&profile_complete=true"
          ),
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const data = await response.json();
        setTalent((data.freelancers || []).map(mapFreelancer));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Failed to load featured talent", error);
          setTalent([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadTalent();
    return () => controller.abort();
  }, []);

  if (!loading && talent.length === 0) return null;

  return (
    <section
      id="talent-grid"
      className="border-t-[1.5px] border-ink-v2 bg-white px-6 py-24 lg:px-10"
    >
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-11 flex flex-col gap-6 min-[900px]:flex-row min-[900px]:items-end min-[900px]:justify-between">
          <div>
            <div className="mb-3.5 text-[12px] font-bold uppercase leading-none tracking-[0.12em] text-coral-500">
              The marketplace
            </div>
            <h2 className="mb-3 text-balance text-[clamp(32px,4vw,48px)] font-bold leading-[1.05] tracking-[-0.025em]">
              Meet a few of the <Highlight>top 5%</Highlight>.
            </h2>
            <p className="max-w-[520px] text-[20px] font-medium leading-[1.5] text-warm-600">
              Real people, real rates, real availability. Every profile passed a
              portfolio review, live work trial and reference check.
            </p>
          </div>

          <div className="flex shrink-0 gap-2.5">
            <button
              type="button"
              onClick={() => scrollByPage(-1)}
              disabled={!canScrollLeft}
              aria-label="Previous freelancers"
              className="flex h-12 w-12 items-center justify-center rounded-full border-[1.5px] border-ink-v2 bg-white text-[18px] font-bold leading-none text-ink-v2 transition-all duration-120 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:bg-[#FFF3EF] hover:shadow-pop-sm disabled:pointer-events-none disabled:opacity-35"
            >
              &larr;
            </button>
            <button
              type="button"
              onClick={() => scrollByPage(1)}
              disabled={!canScrollRight}
              aria-label="Next freelancers"
              className="flex h-12 w-12 items-center justify-center rounded-full border-[1.5px] border-ink-v2 bg-white text-[18px] font-bold leading-none text-ink-v2 transition-all duration-120 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:bg-[#FFF3EF] hover:shadow-pop-sm disabled:pointer-events-none disabled:opacity-35"
            >
              &rarr;
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          onScroll={syncArrows}
          style={{ scrollbarWidth: "none" }}
          className="-mx-6 flex snap-x snap-mandatory gap-6 overflow-x-auto px-6 pb-4 lg:-mx-10 lg:px-10 [&::-webkit-scrollbar]:hidden"
        >
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <TalentCardSkeleton key={i} />
              ))
            : talent.map((t) => (
                <article
                  key={t.id}
                  className={`${SLIDE_CLASS} flex flex-col gap-4 rounded-3xl border-[1.5px] border-ink-v2 bg-cream-100 p-7 transition-all duration-[220ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-pop`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-[52px] w-[52px] shrink-0 items-center justify-center overflow-hidden rounded-full border-[1.5px] border-ink-v2 text-[17px] font-extrabold leading-none text-white ${t.avatarBg}`}
                    >
                      {t.photo ? (
                        <img
                          src={t.photo}
                          alt={t.name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        t.initials
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[17px] font-bold leading-[1.2]">
                        {t.name}
                      </div>
                      <div className="line-clamp-1 text-[14px] leading-[1.5] text-warm-600">
                        {t.role}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 whitespace-nowrap rounded-full border border-ink-v2 px-2.5 py-1.5 text-[11px] font-semibold leading-none tracking-[0.04em] ${t.availBg}`}
                    >
                      {t.avail}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {t.skills.map((s) => (
                      <span
                        key={s}
                        className="rounded-full border border-warm-300 bg-white px-[11px] py-1.5 text-[12px] font-semibold leading-none"
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  {t.videoUrl && (
                    <a
                      href={t.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative block h-[200px] overflow-hidden rounded-2xl border-[1.5px] border-ink-v2 bg-warm-200"
                    >
                      {t.photo && (
                        <img
                          src={t.photo}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      )}
                      <span className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-[7px] rounded-full border border-white/60 bg-ink-v2/75 px-3.5 py-[9px] text-[13px] font-semibold leading-none text-white backdrop-blur-[4px]">
                        &#9654; Intro video &rsaquo;
                      </span>
                    </a>
                  )}

                  <p className="line-clamp-3 text-[14px] leading-[1.5] text-warm-600">
                    {t.blurb}
                  </p>

                  <div className="mt-auto flex items-baseline justify-between gap-3 border-t border-warm-200 pt-3.5">
                    <div>
                      {t.rate && (
                        <>
                          <span className="font-mono text-[16px] font-bold leading-none">
                            {t.rate}
                          </span>
                          <span className="text-[12px] font-medium leading-[1.4] text-warm-500">
                            {" "}
                            /hr
                          </span>
                        </>
                      )}
                    </div>
                    <span className="text-[12px] font-medium leading-[1.4] text-warm-500">
                      {t.loc}
                    </span>
                  </div>

                  <div className="flex gap-2.5">
                    <Link
                      to={`/freelancers/${t.id}`}
                      className="flex-1 rounded-full border-[1.5px] border-ink-v2 bg-white py-[13px] text-center text-[14px] font-bold leading-none text-ink-v2 transition-colors duration-120 hover:bg-[#FFF3EF]"
                    >
                      View profile
                    </Link>
                    <a
                      href="#cta"
                      className="flex-1 rounded-full border-[1.5px] border-ink-v2 bg-coral-500 py-[13px] text-center text-[14px] font-bold leading-none text-white transition-colors duration-120 hover:bg-[#EB4323] hover:text-white"
                    >
                      Match to a job
                    </a>
                  </div>
                </article>
              ))}
        </div>

        <div className="mt-11 flex justify-center">
          <Link
            to="/talent"
            className="rounded-full border-[1.5px] border-ink-v2 bg-white px-[30px] py-4 text-[15px] font-bold leading-none text-ink-v2 shadow-pop-sm transition-all duration-120 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-x-px hover:-translate-y-0.5 hover:shadow-pop active:translate-x-0.5 active:translate-y-[3px] active:shadow-none"
          >
            Browse all freelancers &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}
